import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { garantirUsuario } from '@/lib/usuario';
import { guardarDestino } from '@/lib/destino';
import { useSituacoesInscrito } from '@/lib/situacaoInscrito';
import CartaoInscricao from './components/CartaoInscricao';
import ListaEntrada from '@/components/movimento/ListaEntrada';
import Dica from '@/components/feature/Dica';
import Accordion from '@/components/ui/Accordion';
import type { GrupoEvento, Inscrito, ItemInscricao } from './types';

interface SaldoLinha {
  inscricao_id: number;
  saldo_centavos: number;
}

interface EventoFim {
  id: number;
  fim_em: string | null;
  vende_ingressos: boolean;
}

function agruparInscricoes(
  lista: Inscrito[],
  saldoMap: Map<number, number>,
  fimMap: Map<number, string | null>,
  meusIds: Set<number>,
): GrupoEvento[] {
  const agora = Date.now();
  const mapa = new Map<number, GrupoEvento>();

  lista.forEach((inscrito) => {
    if (!mapa.has(inscrito.evento_id)) {
      const fim = fimMap.get(inscrito.evento_id);
      const anterior = fim ? new Date(fim).getTime() < agora : false;
      mapa.set(inscrito.evento_id, {
        eventoId: inscrito.evento_id,
        eventoNome: inscrito.evento,
        eventoSlug: inscrito.evento_slug,
        inicioEm: inscrito.evento_inicio_em,
        anterior,
        itens: [],
      });
    }
    const item: ItemInscricao = {
      inscrito,
      saldo: saldoMap.get(inscrito.inscricao_id) ?? null,
      propria: meusIds.has(inscrito.participante_id),
    };
    mapa.get(inscrito.evento_id)!.itens.push(item);
  });

  const grupos = [...mapa.values()];
  grupos.forEach((grupo) => {
    grupo.itens.sort(
      (a, b) =>
        new Date(a.inscrito.criado_em).getTime() - new Date(b.inscrito.criado_em).getTime(),
    );
  });

  grupos.sort((a, b) => {
    if (a.anterior !== b.anterior) return a.anterior ? 1 : -1;
    return new Date(a.inicioEm).getTime() - new Date(b.inicioEm).getTime();
  });

  return grupos;
}

export default function MinhasInscricoesPage() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [grupos, setGrupos] = useState<GrupoEvento[]>([]);
  const [aguardando, setAguardando] = useState<Set<string>>(new Set());
  // Modalidades que entram pela senha da fila (Campista): o selo é só "Inscrito".
  const [senhaMods, setSenhaMods] = useState<Set<number>>(new Set());
  const [reload, setReload] = useState(0);

  // Uma consulta por inscrição exibida (em paralelo). Enquanto não chega, o
  // cartão mostra o selo neutro — nunca o status real antes da resposta.
  const idsInscricoes = useMemo(
    () => grupos.flatMap((g) => g.itens.map((i) => i.inscrito.inscricao_id)),
    [grupos],
  );
  const { situacoes, carregando: carregandoSituacoes } = useSituacoesInscrito(idsInscricoes);

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      guardarDestino();
      navigate(`/entrar?voltar=${encodeURIComponent('/minhas-inscricoes')}`, { replace: true });
      return;
    }
    let ativo = true;

    async function carregar() {
      setLoading(true);
      setErro(null);
      try {
        await garantirUsuario();

        const { data: inscritosData, error: errInscritos } = await supabase
          .from('vw_inscritos')
          .select('*')
          .eq('responsavel_id', user.id);
        if (errInscritos) throw errInscritos;

        const lista = (inscritosData as Inscrito[]) || [];

        // Participantes que são a própria pessoa logada (para separar as
        // inscrições dela das que ela fez para outras pessoas).
        const meusIds = new Set<number>();
        const { data: meusParticipantes } = await supabase
          .from('participantes')
          .select('id')
          .eq('usuario_id', user.id);
        ((meusParticipantes as { id: number }[]) || []).forEach((p) => meusIds.add(p.id));

        const { data: saldosData, error: errSaldos } = await supabase
          .from('vw_saldo_inscricao')
          .select('inscricao_id, saldo_centavos')
          .eq('responsavel_id', user.id);
        if (errSaldos) throw errSaldos;

        const saldoMap = new Map<number, number>();
        ((saldosData as SaldoLinha[]) || []).forEach((linha) => {
          saldoMap.set(linha.inscricao_id, linha.saldo_centavos);
        });

        const eventoIds = [...new Set(lista.map((i) => i.evento_id))];
        const fimMap = new Map<number, string | null>();
        const vendeMap = new Map<number, boolean>();
        if (eventoIds.length > 0) {
          const { data: eventosData, error: errEventos } = await supabase
            .from('eventos')
            .select('id, fim_em, vende_ingressos')
            .in('id', eventoIds);
          if (errEventos) throw errEventos;
          ((eventosData as EventoFim[]) || []).forEach((evento) => {
            fimMap.set(evento.id, evento.fim_em);
            vendeMap.set(evento.id, evento.vende_ingressos);
          });
        }

        // Só inscrições: descarta toda inscrição cujo evento vende ingresso.
        const listaFiltrada = lista.filter((i) => !(vendeMap.get(i.evento_id) ?? false));

        // Inscrições presenciais aguardando a bipagem: mostram "Aguardando validação".
        const aguardandoSet = new Set<string>();
        const senhaSet = new Set<number>();
        if (listaFiltrada.length > 0) {
          const { data: presData } = await supabase
            .from('inscricoes')
            .select('codigo, validada_em, modalidade_id')
            .eq('responsavel_id', user.id);
          const presencialMods = new Set<number>();
          const modIds = [
            ...new Set(
              ((presData as { modalidade_id: number }[]) || []).map((r) => r.modalidade_id),
            ),
          ];
          if (modIds.length > 0) {
            const { data: modsData } = await supabase
              .from('modalidades')
              .select('id')
              .in('id', modIds)
              .eq('validacao_presencial', true);
            ((modsData as { id: number }[]) || []).forEach((m) => presencialMods.add(m.id));
          }
          ((presData as { codigo: string; validada_em: string | null; modalidade_id: number }[]) ||
            []).forEach((r) => {
            if (presencialMods.has(r.modalidade_id) && !r.validada_em) {
              aguardandoSet.add(r.codigo);
            }
          });

          // Formulário ativo com a pergunta "senha_fila" = modalidade que usa senha.
          if (modIds.length > 0) {
            const { data: formsData } = await supabase
              .from('formularios')
              .select('id, modalidade_id')
              .in('modalidade_id', modIds)
              .eq('ativo', true);
            const forms =
              (formsData as { id: number; modalidade_id: number }[] | null) || [];
            if (forms.length > 0) {
              const { data: pergData } = await supabase
                .from('perguntas')
                .select('formulario_id')
                .in(
                  'formulario_id',
                  forms.map((f) => f.id),
                )
                .eq('chave', 'senha_fila');
              const comSenha = new Set(
                ((pergData as { formulario_id: number }[] | null) || []).map(
                  (r) => r.formulario_id,
                ),
              );
              forms.forEach((f) => {
                if (comSenha.has(f.id)) senhaSet.add(f.modalidade_id);
              });
            }
          }
        }

        if (ativo) {
          setGrupos(agruparInscricoes(listaFiltrada, saldoMap, fimMap, meusIds));
          setAguardando(aguardandoSet);
          setSenhaMods(senhaSet);
        }
      } catch (e) {
        if (ativo) {
          setErro(
            mensagemAmigavel(e, 'Não foi possível carregar suas inscrições. Tente novamente.'),
          );
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [navigate, reload, pronto, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-4 py-8">
          <div className="h-8 w-56 animate-pulse rounded bg-secondary-100" />
          <div className="mt-2 h-4 w-72 animate-pulse rounded bg-secondary-100" />
          <div className="mt-6 space-y-4">
            <div className="h-4 w-40 animate-pulse rounded bg-secondary-100" />
            <div className="h-24 w-full animate-pulse rounded-lg bg-secondary-100" />
            <div className="h-24 w-full animate-pulse rounded-lg bg-secondary-100" />
          </div>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-6 text-center">
            <h1 className="text-xl font-normal text-foreground-950">Não foi possível carregar</h1>
            <p className="mt-2 text-sm text-secondary-500">{erro}</p>
            <button
              type="button"
              onClick={() => setReload((r) => r + 1)}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Tentar de novo
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (grupos.length === 0) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-4 py-8">
          <div className="flex items-center gap-2"><h1 className="text-[28px] font-normal text-foreground-950">Minhas inscrições</h1><Dica id="minhas-inscricoes" /></div>
          <p className="mt-1 text-sm text-secondary-500">
            Aqui ficam as suas inscrições e as das pessoas que você inscreveu.
          </p>

          <div className="mt-6 overflow-hidden rounded-lg border border-secondary-200 bg-white">
            <div className="h-[10px] w-full bg-primary-500" />
            <div className="p-8 text-center">
              <p className="text-[15px] text-foreground-800">
                Você ainda não tem inscrições.
              </p>
              <Link
                to="/meus-ingressos"
                className="mt-4 inline-block cursor-pointer text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                Ver meus ingressos
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Primeiro as inscrições da própria pessoa; depois as feitas para outras.
  const proprios = grupos
    .map((g) => ({ ...g, itens: g.itens.filter((i) => i.propria) }))
    .filter((g) => g.itens.length > 0);
  const deOutros = grupos
    .map((g) => ({ ...g, itens: g.itens.filter((i) => !i.propria) }))
    .filter((g) => g.itens.length > 0);
  const totalOutros = deOutros.reduce((soma, g) => soma + g.itens.length, 0);

  const propriosAtuais = proprios.filter((g) => !g.anterior);
  const propriosAnteriores = proprios.filter((g) => g.anterior);
  const totalAnteriores = propriosAnteriores.reduce((soma, g) => soma + g.itens.length, 0);

  return (
    <div className="min-h-screen bg-background-100">
      <div className="mx-auto w-full max-w-[720px] px-4 py-8">
        <div className="flex items-center gap-2"><h1 className="text-[28px] font-normal text-foreground-950">Minhas inscrições</h1><Dica id="minhas-inscricoes" /></div>
        <p className="mt-1 text-sm text-secondary-500">
          Primeiro as suas inscrições; depois as que você fez para outras pessoas.
        </p>

        {propriosAtuais.length > 0 && (
          <div className="mt-6 space-y-6">
            {propriosAtuais.map((grupo) => (
              <section key={grupo.eventoId}>
                <div className="flex items-center justify-between gap-3">
                  <h2 className="min-w-0 truncate text-[14px] font-semibold text-foreground-800">
                    {grupo.eventoNome}
                  </h2>
                  <Link
                    to={`/inscricao/${grupo.itens[0].inscrito.modalidade_id}`}
                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-600"
                  >
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-add-line" />
                    </span>
                    Nova inscrição
                  </Link>
                </div>
                <ListaEntrada
                  itens={grupo.itens}
                  chave={(item) => item.inscrito.inscricao_id}
                  className="mt-2 space-y-3"
                >
                  {(item) => (
                    <CartaoInscricao
                      inscrito={item.inscrito}
                      saldo={item.saldo}
                      anterior={false}
                      aguardandoValidacao={aguardando.has(item.inscrito.codigo)}
                      usaSenha={senhaMods.has(item.inscrito.modalidade_id)}
                      situacao={situacoes.get(item.inscrito.inscricao_id)}
                      carregandoSituacao={carregandoSituacoes}
                    />
                  )}
                </ListaEntrada>
              </section>
            ))}
          </div>
        )}

        {totalOutros > 0 && (
          <div className="mt-6">
            <Accordion titulo="Feitas por você para outras pessoas" resumo={`${totalOutros}`}>
              <div className="space-y-5">
                {deOutros.map((grupo) => (
                  <section key={grupo.eventoId}>
                    <h3 className="truncate text-[14px] font-semibold text-foreground-800">
                      {grupo.eventoNome}
                    </h3>
                    <ListaEntrada
                      itens={grupo.itens}
                      chave={(item) => item.inscrito.inscricao_id}
                      className="mt-2 space-y-3"
                    >
                      {(item) => (
                        <CartaoInscricao
                          inscrito={item.inscrito}
                          saldo={item.saldo}
                          anterior={grupo.anterior}
                          aguardandoValidacao={
                            !grupo.anterior && aguardando.has(item.inscrito.codigo)
                          }
                          usaSenha={senhaMods.has(item.inscrito.modalidade_id)}
                          situacao={situacoes.get(item.inscrito.inscricao_id)}
                          carregandoSituacao={carregandoSituacoes}
                        />
                      )}
                    </ListaEntrada>
                  </section>
                ))}
              </div>
            </Accordion>
          </div>
        )}

        {totalAnteriores > 0 && (
          <div className="mt-6">
            <Accordion titulo="Anteriores" resumo={`${totalAnteriores}`}>
              <div className="space-y-5">
                {propriosAnteriores.map((grupo) => (
                  <section key={grupo.eventoId}>
                    <h3 className="truncate text-[14px] font-semibold text-foreground-800">
                      {grupo.eventoNome}
                    </h3>
                    <ListaEntrada
                      itens={grupo.itens}
                      chave={(item) => item.inscrito.inscricao_id}
                      className="mt-2 space-y-3"
                    >
                      {(item) => (
                        <CartaoInscricao
                          inscrito={item.inscrito}
                          saldo={item.saldo}
                          anterior
                          usaSenha={senhaMods.has(item.inscrito.modalidade_id)}
                          situacao={situacoes.get(item.inscrito.inscricao_id)}
                          carregandoSituacao={carregandoSituacoes}
                        />
                      )}
                    </ListaEntrada>
                  </section>
                ))}
              </div>
            </Accordion>
          </div>
        )}
      </div>
    </div>
  );
}